import React, {useState, useEffect, useRef, useCallback} from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import styled from '@emotion/styled'
import {toast} from 'react-toastify'
import {useAuthClient} from '../utils/use-auth-client'
import {ReactComponent as RefreshIcon} from '../assets/refresh-icon.svg'
import {theme} from '../pages/ProposalTool/features/Dashboard/theme'
import {
  ContentArea,
  MainArea,
  ModalOverlay,
  ModalBox,
  ModalCancelBtn,
  ModalActions,
  FormInput,
  FormLabel,
  FormField,
} from '../pages/ProposalTool/features/Dashboard/styled.components'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  EmptyStateTitle,
} from '../pages/ProposalTool/components/ProposalTable/styled.components'
import {
  DateRangeWrapper,
  DateRangeTrigger,
  CalendarPopout,
} from '../pages/ProposalTool/components/ProposalFilters/styled.components'
import {ProposalPagination} from '../pages/ProposalTool/components/ProposalPagination'
import {PTCalender, PTCaretDown} from '../assets'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const DEFAULT_TIMEZONE = 'America/Chicago'

const FiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 12px;
`

const ClearBtn = styled(ModalCancelBtn)`
  white-space: nowrap;
  background: #f4f6fb;
  border-color: #0327a9;
  color: #0327a9;

  &:hover {
    background: #e8ecf9;
  }
`

const formatDate = dateStr => {
  if (!dateStr) return 'N/A'
  const d = dayjs(dateStr)
  return d.isValid()
    ? d.tz(DEFAULT_TIMEZONE).format('MM/DD/YYYY hh:mmA z')
    : 'Invalid date'
}

// ─── Debounce Hook ────────────────────────────────────────────────────────────

const useDebouncedValue = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

// ─── Order Details Modal ──────────────────────────────────────────────────────

const DetailModalBox = styled(ModalBox)`
  max-width: 500px;
  width: 94%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
  background: #ffffff;
`

const ModalScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 24px;
  scrollbar-gutter: stable;
  min-height: 180px;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.border};
`

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.text};
  font-family: ${theme.font.family};
`

const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${theme.colors.muted};
  font-family: ${theme.font.family};
`

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  font-size: 20px;
  cursor: pointer;
  color: ${theme.colors.muted};
  line-height: 1;
  width: 28px;
  height: 28px;
  border-radius: ${theme.radius.sm};
  transition: background ${theme.transition.fast};

  &:hover {
    background: ${theme.colors.hover};
    color: ${theme.colors.text};
  }

  &:focus {
    outline: none;
  }
`

const InfoRow = ({label, value}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.colors.border}`,
      gap: '16px',
    }}
  >
    <span
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: theme.colors.text,
        fontFamily: theme.font.family,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: '13px',
        color: theme.colors.muted,
        fontFamily: theme.font.family,
        textAlign: 'right',
        wordBreak: 'break-all',
      }}
    >
      {value}
    </span>
  </div>
)

const OrderDetailsModal = ({order, onClose}) => {
  if (!order) return null

  return (
    <ModalOverlay onClick={onClose}>
      <DetailModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>Order Details</ModalTitle>
            <ModalSubtitle>Order #{order.order_id}</ModalSubtitle>
          </div>
          <CloseBtn onClick={onClose}>
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </CloseBtn>
        </ModalHeader>

        <ModalScrollContent>
          {[
            {
              label: 'Order Completed',
              value: formatDate(order.order_completed_date),
            },
            {label: 'Client Email', value: order.client_email || 'N/A'},
            {label: 'Thryv ID', value: order.thryv_id || 'N/A'},
            {label: 'Company Name', value: order.company_name || 'N/A'},
          ].map(({label, value}) => (
            <InfoRow key={label} label={label} value={value} />
          ))}
        </ModalScrollContent>

        <ModalActions
          style={{
            padding: '16px 0 24px',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          <ClearBtn type="button" onClick={onClose}>
            Close
          </ClearBtn>
        </ModalActions>
      </DetailModalBox>
    </ModalOverlay>
  )
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

const TableBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${theme.radius.full};
  border: 1px solid ${theme.colors.border};
  background: #f4f6fb;
  font-size: 12px;
  font-weight: 500;
  font-family: ${theme.font.family};
  color: ${theme.colors.text};
  cursor: pointer;
  white-space: nowrap;
  transition: background ${theme.transition.fast};

  &:hover:not(:disabled) {
    background: ${theme.colors.hover};
  }
`

const COUNT_PER_PAGE = 15

function CompletedLinks() {
  const client = useAuthClient()

  const [rows, setRows] = useState([])
  const [perPage, setPerPage] = useState(COUNT_PER_PAGE)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false,
  })
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
  const dateRangeRef = useRef(null)

  const [filters, setFilters] = useState({
    companyName: '',
    clientEmail: '',
    thryvId: '',
    startDate: '',
    endDate: '',
  })

  const debouncedCompanyName = useDebouncedValue(filters.companyName)
  const debouncedClientEmail = useDebouncedValue(filters.clientEmail)
  const debouncedThryvId = useDebouncedValue(filters.thryvId)
  const debouncedStartDate = useDebouncedValue(filters.startDate)
  const debouncedEndDate = useDebouncedValue(filters.endDate)

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value}
      if (key === 'startDate' && prev.endDate && value > prev.endDate) {
        updated.endDate = ''
      }
      return updated
    })
  }

  const clearFilters = () => {
    setFilters({
      companyName: '',
      clientEmail: '',
      thryvId: '',
      startDate: '',
      endDate: '',
    })
    setIsDateRangeOpen(false)
    setPage(1)
  }

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        isDateRangeOpen &&
        dateRangeRef.current &&
        !dateRangeRef.current.contains(e.target)
      ) {
        setIsDateRangeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDateRangeOpen])

  useEffect(() => {
    setPage(1)
  }, [
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
  ])

  const fetchCompletedLinks = useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: perPage.toString(),
      })
      if (debouncedCompanyName)
        params.append('companyName', debouncedCompanyName)
      if (debouncedClientEmail)
        params.append('clientEmail', debouncedClientEmail)
      if (debouncedThryvId) params.append('thryvId', debouncedThryvId)
      if (debouncedStartDate) params.append('startDate', debouncedStartDate)
      if (debouncedEndDate) params.append('endDate', debouncedEndDate)

      const data = await client(`cc/order/completed-links?${params}`)

      if (data.success && data.response) {
        setRows(data.response.data || [])
        if (data.response.pagination) {
          setPagination({
            page: data.response.pagination.page,
            totalPages: data.response.pagination.totalPages,
            total: data.response.pagination.total,
            hasNext: data.response.pagination.hasNext,
            hasPrev: data.response.pagination.hasPrev,
          })
        }
      }
    } catch (err) {
      console.error('Error fetching completed links:', err)
      toast.error('Error fetching completed links')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [
    client,
    page,
    debouncedCompanyName,
    debouncedClientEmail,
    debouncedThryvId,
    debouncedStartDate,
    debouncedEndDate,
    perPage,
  ])

  useEffect(() => {
    fetchCompletedLinks()
  }, [fetchCompletedLinks])

  const handleDateRangeChange = value => {
    if (Array.isArray(value)) {
      const [start, end] = value
      updateFilter('startDate', start ? dayjs(start).format('YYYY-MM-DD') : '')
      updateFilter('endDate', end ? dayjs(end).format('YYYY-MM-DD') : '')
      if (start && end) setIsDateRangeOpen(false)
    }
  }

  const getCalendarValue = () => {
    if (!filters.startDate && !filters.endDate) return null
    const toLocal = str => {
      if (!str) return null
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    }
    return [toLocal(filters.startDate), toLocal(filters.endDate)]
  }

  const dateRangeLabel = filters.startDate
    ? filters.endDate
      ? `${dayjs(filters.startDate).format('MM/DD/YYYY')} - ${dayjs(
          filters.endDate,
        ).format('MM/DD/YYYY')}`
      : `${dayjs(filters.startDate).format('MM/DD/YYYY')} - Select end date`
    : ''

  // const columns = useMemo(
  //   () => [
  //     {key: 'order_id', label: 'Order ID'},
  //     {
  //       key: 'order_completed_date',
  //       label: 'Completed Date',
  //       render: (_, row) => (
  //         <p
  //           className="formmated-date"
  //           style={{margin: 0, fontSize: '12px', whiteSpace: 'nowrap'}}
  //         >
  //           {formatDate(row.order_completed_date)}
  //         </p>
  //       ),
  //     },
  //     {key: 'client_email', label: 'Client Email'},
  //     {key: 'thryv_id', label: 'Thryv ID'},
  //     {key: 'company_name', label: 'Company Name'},
  //     {
  //       key: 'actions',
  //       label: 'Actions',
  //       render: (_, row) => (
  //         <button
  //           className="table-button"
  //           onClick={e => {
  //             e.stopPropagation()
  //             setSelectedOrder(row)
  //           }}
  //         >
  //           Details
  //         </button>
  //       ),
  //     },
  //   ],
  //   [],
  // )

  const COLUMNS_DEF = [
    {key: 'order_id', label: 'Order ID'},
    {key: 'order_completed_date', label: 'Completed Date'},
    {key: 'client_email', label: 'Client Email'},
    {key: 'thryv_id', label: 'Thryv ID'},
    {key: 'company_name', label: 'Company Name'},
    {key: 'actions', label: 'Actions'},
  ]

  return (
    <ContentArea style={{padding: '12px 16px'}}>
      <MainArea style={{fontSize: 'clamp(11px, 1vw, 13px)'}}>
        <FiltersRow>
          <FormField style={{flex: '1 1 180px', minWidth: '160px'}}>
            <FormLabel>Company Name</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by company"
              value={filters.companyName}
              onChange={e => updateFilter('companyName', e.target.value)}
            />
          </FormField>

          <FormField style={{flex: '1 1 200px', minWidth: '180px'}}>
            <FormLabel>Client Email</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by email"
              value={filters.clientEmail}
              onChange={e => updateFilter('clientEmail', e.target.value)}
            />
          </FormField>

          <FormField style={{flex: '1 1 160px', minWidth: '140px'}}>
            <FormLabel>Thryv ID</FormLabel>
            <FormInput
              type="text"
              placeholder="Filter by Thryv ID"
              value={filters.thryvId}
              onChange={e => updateFilter('thryvId', e.target.value)}
            />
          </FormField>

          <FormField
            style={{flex: '1 1 260px', minWidth: '220px', position: 'relative'}}
          >
            <FormLabel>Date Range</FormLabel>
            <DateRangeWrapper ref={dateRangeRef} style={{width: '100%'}}>
              <DateRangeTrigger
                type="button"
                hasValue={!!(filters.startDate || filters.endDate)}
                onClick={() => setIsDateRangeOpen(prev => !prev)}
              >
                <img
                  src={PTCalender}
                  width="13"
                  alt=""
                  style={{filter: 'brightness(0) invert(0.5)', flexShrink: 0}}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dateRangeLabel || 'MM/DD/YYYY - MM/DD/YYYY'}
                </span>
                {filters.startDate || filters.endDate ? (
                  <span
                    role="button"
                    onClick={e => {
                      e.stopPropagation()
                      updateFilter('startDate', '')
                      updateFilter('endDate', '')
                      setIsDateRangeOpen(false)
                    }}
                    style={{fontSize: '16px', lineHeight: 1, cursor: 'pointer'}}
                  >
                    ×
                  </span>
                ) : (
                  <img
                    src={PTCaretDown}
                    width="10"
                    alt=""
                    style={{
                      filter: 'brightness(0) invert(0.5)',
                      flexShrink: 0,
                      transform: isDateRangeOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                )}
              </DateRangeTrigger>
              {isDateRangeOpen && (
                <CalendarPopout style={{left: 0, right: 'auto'}}>
                  <Calendar
                    selectRange
                    onChange={handleDateRangeChange}
                    value={getCalendarValue()}
                  />
                </CalendarPopout>
              )}
            </DateRangeWrapper>
          </FormField>

          <div style={{display: 'flex', alignItems: 'flex-end', gap: '8px'}}>
            <ClearBtn type="button" onClick={clearFilters}>
              Clear Filters
            </ClearBtn>
            <ClearBtn
              type="button"
              onClick={fetchCompletedLinks}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshIcon
                width="14"
                height="14"
                style={{
                  animation: isLoading ? 'spin 0.8s linear infinite' : 'none',
                }}
              />
              Refresh
            </ClearBtn>
          </div>
        </FiltersRow>

        <TableWrapper>
          <StyledTable>
            <THead>
              <TR>
                {COLUMNS_DEF.map(col => (
                  <TH key={col.key}>{col.label}</TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {isLoading ? (
                Array.from({length: perPage}).map((_, i) => (
                  <TR key={`sk-${i}`}>
                    {COLUMNS_DEF.map(col => (
                      <TD key={col.key}>
                        <div
                          style={{
                            height: '14px',
                            borderRadius: '4px',
                            background:
                              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'skeleton-shimmer 1.5s infinite',
                            width: col.key === 'actions' ? '60px' : '80%',
                          }}
                        />
                      </TD>
                    ))}
                  </TR>
                ))
              ) : rows.length === 0 ? (
                <TR>
                  <TD colSpan={COLUMNS_DEF.length}>
                    <EmptyState>
                      <EmptyStateTitle>
                        No Completed Purchase Links found.
                      </EmptyStateTitle>
                    </EmptyState>
                  </TD>
                </TR>
              ) : (
                rows.map((row, idx) => (
                  <TR key={row.order_id || idx}>
                    <TD>{row.order_id}</TD>
                    <TD>
                      <p
                        style={{
                          margin: 0,
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatDate(row.order_completed_date)}
                      </p>
                    </TD>
                    <TD>{row.client_email || 'N/A'}</TD>
                    <TD>{row.thryv_id || 'N/A'}</TD>
                    <TD>{row.company_name || 'N/A'}</TD>
                    <TD>
                      <TableBtn
                        onClick={e => {
                          e.stopPropagation()
                          setSelectedOrder(row)
                        }}
                      >
                        Details
                      </TableBtn>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </StyledTable>
        </TableWrapper>

        {pagination && pagination.total > 0 && (
          <ProposalPagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={value => {
              setPerPage(value)
              setPage(1)
            }}
          />
        )}
      </MainArea>

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </ContentArea>
  )
}

export {CompletedLinks}
